// src/components/store/sections/featured-products/FramedProductCard.tsx
"use client";

import { useState } from "react";
import Image from "next/image";
import Link from "next/link";
import { Heart, Star } from "lucide-react";
import { useCart } from "@/context/CartContext";

export interface ProductItem {
  id: number | string;
  name: string;
  slug?: string;
  price: number | string;
  regular_price?: number | string;
  image?: string | null;
  primary_image?: string | null;
  category_name?: string;
  description?: string;
  rating?: number;
}

const CARD_THEMES = [
  {
    frameColor: "#ef4444", // Crimson Red
    titleColor: "#dc2626",
    tagText: "Men's Fashions",
    clipPath: "polygon(0 0, 100% 0, 100% calc(100% - 16px), 0 100%)",
  },
  {
    frameColor: "#8b5cf6", // Vibrant Purple
    titleColor: "#7c3aed",
    tagText: "Women's Fashion",
    clipPath: "polygon(0 0, 100% 0, 100% 100%, 0 calc(100% - 16px))",
  },
  {
    frameColor: "#0ea5e9", // Sky Blue
    titleColor: "#0284c7",
    tagText: "Men's Fashions",
    clipPath: "polygon(0 0, 100% 0, 100% calc(100% - 16px), 0 100%)",
  },
  {
    frameColor: "#10b981", // Emerald Green
    titleColor: "#059669",
    tagText: "Kids Fashion",
    clipPath: "polygon(0 0, 100% 0, 100% 100%, 0 calc(100% - 16px))",
  },
];

interface FramedProductCardProps {
  product: ProductItem;
  index?: number;
}

export default function FramedProductCard({
  product,
  index = 0,
}: FramedProductCardProps) {
  const [quantity, setQuantity] = useState(1);
  const [isWishlisted, setIsWishlisted] = useState(true);
  const cartContext = useCart?.() as any;

  const theme = CARD_THEMES[index % CARD_THEMES.length];

  const formatImageUrl = (img?: string | null) => {
    if (!img) return null;
    if (img.startsWith("http://") || img.startsWith("https://")) return img;
    return `http://127.0.0.1:8000${img.startsWith("/") ? "" : "/"}${img}`;
  };

  const productImg =
    formatImageUrl(product.primary_image || product.image) ||
    "https://images.unsplash.com/photo-1542291026-7eec264c27ff?w=600&auto=format&fit=crop&q=80";

  const handleAddToCart = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (cartContext && typeof cartContext.addToCart === "function") {
      cartContext.addToCart(product, quantity);
    } else {
      window.dispatchEvent(
        new CustomEvent("addToCart", { detail: { product, quantity } })
      );
      window.dispatchEvent(new CustomEvent("openCartDrawer"));
    }
  };

  const priceNum = Number(product.price) || 0;

  return (
    <div className="w-full bg-white rounded-2xl sm:rounded-3xl border border-slate-200/90 shadow-sm hover:shadow-lg transition-all duration-300 overflow-hidden flex flex-col justify-between group select-none">
      <div>
        {/* 🌟 ১. টপ কালারড জিওমেট্রিক ফ্রেম */}
        <div
          className="relative p-2.5 sm:p-3 pb-5 transition-colors duration-300"
          style={{
            backgroundColor: theme.frameColor,
            clipPath: theme.clipPath,
          }}
        >
          {/* ফ্রেমের ভেতরের রাউন্ডেড প্রোডাক্ট ইমেজ (১০০% ফুল-ব্লিড ফিট) */}
          <div className="relative w-full h-44 sm:h-48 md:h-52 rounded-xl sm:rounded-2xl overflow-hidden bg-slate-900/10 shadow-inner">
            <Image
              src={productImg}
              alt={product.name}
              fill
              unoptimized
              className="object-cover group-hover:scale-105 transition-transform duration-500"
            />
          </div>
        </div>

        {/* 🌟 ২. কার্ড বডি কনটেন্ট */}
        <div className="p-3.5 sm:p-4 pt-2 space-y-2.5">
          {/* ক্যাটাগরি পিল ও উইশলিস্ট হার্ট */}
          <div className="flex items-center justify-between gap-1">
            <span className="text-[10px] sm:text-[11px] font-semibold text-slate-700 px-2.5 py-0.5 rounded-md border border-slate-200 bg-white truncate max-w-[80%]">
              {product.category_name || theme.tagText}
            </span>

            <button
              type="button"
              onClick={() => setIsWishlisted(!isWishlisted)}
              className="p-1 hover:scale-110 active:scale-95 transition-transform cursor-pointer shrink-0"
              title="Add to Wishlist"
            >
              <Heart
                className={`w-4 h-4 transition-colors ${
                  isWishlisted
                    ? "fill-amber-400 text-amber-400"
                    : "text-slate-300 hover:text-amber-400"
                }`}
              />
            </button>
          </div>

          {/* বোল্ড কালারড টাইটেল */}
          <Link href={`/products/${product.slug || product.id}`} className="block">
            <h3
              style={{ color: theme.titleColor }}
              className="text-xs sm:text-sm font-black uppercase tracking-tight truncate hover:underline"
              title={product.name}
            >
              {product.name}
            </h3>
          </Link>

          {/* শর্ট ডেসক্রিপশন */}
          <p className="text-[11px] text-slate-500 line-clamp-2 leading-relaxed min-h-[32px]">
            {product.description ||
              "Very Comfortable product of authentic materials and modern styling."}
          </p>

          {/* স্টার রেটিং */}
          <div className="flex items-center gap-0.5 text-amber-400">
            {[1, 2, 3, 4, 5].map((s) => (
              <Star
                key={s}
                className={`w-3 h-3 ${
                  s <= Math.round(product.rating || 5)
                    ? "fill-amber-400 text-amber-400"
                    : "fill-slate-200 text-slate-300"
                }`}
              />
            ))}
          </div>

          {/* প্রাইস ও কোয়ান্টিটি কাউন্টার */}
          <div className="flex items-center justify-between pt-1">
            <div className="text-lg sm:text-xl font-black text-slate-900 tracking-tight">
              ৳{Math.round(priceNum).toLocaleString()}
            </div>

            {/* কোয়ান্টিটি কাউন্টার [-] 1 [+] */}
            <div className="flex items-center border border-slate-200 rounded-lg overflow-hidden bg-white text-[11px] font-bold shadow-2xs">
              <button
                type="button"
                onClick={() => setQuantity((prev) => Math.max(1, prev - 1))}
                className="px-2 py-1 text-slate-500 hover:bg-slate-100 hover:text-slate-800 transition-colors cursor-pointer select-none"
              >
                -
              </button>
              <span className="px-2 py-1 text-slate-900 font-black min-w-[20px] text-center">
                {quantity}
              </span>
              <button
                type="button"
                onClick={() => setQuantity((prev) => prev + 1)}
                className="px-2 py-1 text-slate-500 hover:bg-slate-100 hover:text-slate-800 transition-colors cursor-pointer select-none"
              >
                +
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* 🌟 ৩. ফুল-উইডথ গোল্ডেন "ADD TO CART" বাটন */}
      <div className="p-3.5 sm:p-4 pt-0">
        <button
          type="button"
          onClick={handleAddToCart}
          className="w-full py-2.5 rounded-xl bg-[#f59e0b] hover:bg-[#d97706] active:bg-[#b45309] text-slate-950 font-black text-[11px] uppercase tracking-wider transition-all shadow-sm active:scale-[0.98] cursor-pointer flex items-center justify-center gap-1"
        >
          <span>ADD TO CART</span>
        </button>
      </div>
    </div>
  );
}